import { CloseIcon } from '@/assets/icons/button';
import React, { ReactNode, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  contentLabel: string | ReactNode;
  contentSubtitle?: string;
  version?: string;
  children: ReactNode;
  /** Optional content to render pinned at the bottom of the modal. */
  footer?: ReactNode;
  className?: string;
  closeOnClickOff?: boolean;
  hideCloseButton?: boolean;
}

// Using defaultProps for functional components will be deprecated soon. Write default values inline if needed.
function ModalComponent({
  isOpen,
  onRequestClose,
  contentLabel,
  contentSubtitle,
  version,
  children,
  className,
  closeOnClickOff,
  footer,
  hideCloseButton,
}: Props): React.JSX.Element | null {
  const contentRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const onRequestCloseRef = useRef(onRequestClose);

  useEffect(() => {
    onRequestCloseRef.current = onRequestClose;
  }, [onRequestClose]);

  // Deliberately keyed on isOpen only, not onRequestClose, so this doesn't
  // re-steal focus on every re-render while the modal stays open.
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedElementRef.current = document.activeElement as HTMLElement | null;
    contentRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onRequestCloseRef.current();
        return;
      }

      if (e.key !== 'Tab' || !contentRef.current) return;

      const focusable = Array.from(
        contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElementRef.current?.focus?.();
      previouslyFocusedElementRef.current = null;
    };
  }, [isOpen]);

  // Detect if consumer passed an explicit height / max-height utility so we do not override it
  const userHasHeightClass = className?.match(/(?:^|\s)(h-|max-h-)/);

  // Constrain overall modal height to the viewport (90vh) and allow vertical scrolling when content overflows.
  // This keeps existing behaviour for large desktop resolutions while gracefully degrading on smaller screens.
  const defaultStyle = `flex flex-col relative bg-surface_gray_50 border border-border_gray_200 rounded-2xl shadow-lg z-10 lg:max-w-[1112px] max-w-screen-md m-4 ${
    userHasHeightClass ? '' : 'max-h-[90vh]'
  }`;

  const onClickOff = (): void => {
    if (closeOnClickOff === false) {
      return;
    }
    onRequestClose();
  };

  const modalJsx = (
    <div
      className={`fixed top-0 left-0 w-full h-full flex items-center justify-center z-[1000] ${isOpen ? '' : 'hidden'}`}
    >
      <div
        className='fixed top-0 left-0 w-full h-full bg-background_overlay'
        onClick={onClickOff}
      ></div>
      <div
        ref={contentRef}
        tabIndex={-1}
        role='dialog'
        aria-modal='true'
        aria-label={typeof contentLabel === 'string' ? contentLabel : undefined}
        className={`${defaultStyle} ${className} outline-none`}
      >
        {!hideCloseButton && (
          <div className='flex h-[52px] py-2 px-[10px]'>
            <button
              className='ml-auto mr-0 w-[36px] h-[36px] p-[7px] hover:bg-primary_highlight dark:hover:bg-primary_dark rounded-full transition-colors'
              aria-label='Close modal'
              onClick={onRequestClose}
            >
              <CloseIcon className='w-[22px] h-[22px]' />
            </button>
          </div>
        )}
        {/* pt-8 compensates for the vertical space the close button row would have taken up */}
        <div
          className={`flex justify-center w-full items-center px-8 ${hideCloseButton ? 'pt-8' : ''}`}
        >
          <div className='flex flex-col gap-0 w-full'>
            {typeof contentLabel === 'string' && contentLabel !== '' ? (
              <div className='w-full flex items-center justify-start gap-3'>
                <h2 className='m-0 p-0 max-h-full text-left font-semibold text-2xl leading-8 tracking-[0%]'>
                  {contentLabel}
                </h2>
                {version && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#1570EF',
                      background: '#EFF6FF',
                      borderRadius: 6,
                      padding: '2px 8px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {version}
                  </span>
                )}
              </div>
            ) : (
              <div className='w-full flex justify-center'>{contentLabel}</div>
            )}
            {contentSubtitle && contentSubtitle !== '' && (
              <div className='w-full mt-1'>
                <p className='font-normal text-base leading-6 tracking-[0%]'>{contentSubtitle}</p>
              </div>
            )}
          </div>
        </div>
        {(contentLabel !== '' || contentSubtitle) && (
          <div className='w-full px-[21px] py-4'>
            <hr className='border-t-2 border-gray-300' />
          </div>
        )}
        {/* Scrollable body */}
        <div className='flex flex-col px-8 pt-2 pb-4 flex-1 overflow-y-auto overflow-x-visible'>
          {children}
        </div>

        {/* Optional footer */}
        {footer && <div className='w-full px-8 pb-4'>{footer}</div>}
      </div>
    </div>
  );

  return typeof window !== 'undefined' ? ReactDOM.createPortal(modalJsx, document.body) : null;
}

export default ModalComponent;
